import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectAssetBucketingHeadline,
  selectIsHiddenLeversRTQ,
} from '../CommonSelectors/CommonAreaSelector';
import { WorkflowIdEnum } from '../Workflow/api/useWorkflowDataQuery';
import { useClientStartPageQuery, WorkflowEnum } from './api/useClientStartPageQuery';

export const FINANCIAL_BEHAVIOUR_WORKFLOWS = [
  WorkflowEnum.AssetBucketing,
  WorkflowEnum.RiskTolerance,
  WorkflowEnum.BeFi20,
  WorkflowEnum.PulseCheck,
];

interface OnTileClickParams {
  isCompleted: boolean;
}

interface TileProps {
  name: string;
  description?: string;
  icon: string;
  isCustomIcon?: boolean;
  onClick(params: OnTileClickParams): void;
}

export const useClientStartPage = ({ clientId }) => {
  const queryResult = useClientStartPageQuery({ clientId });
  const isHiddenLeversRTQ = useSelector(selectIsHiddenLeversRTQ);

  const assetBucketingHeadline = useSelector(selectAssetBucketingHeadline);

  const workflowTilesMap: Record<WorkflowEnum, TileProps> = {
    [WorkflowEnum.Comprehensive]: {
      name: 'Comprehensive Plan',
      icon: 'far fa-flag',
      onClick: () => {
        ADV.util.Router.go2('Workflow', {
          clientId,
          workflowId: WorkflowIdEnum.Comprehensive,
          stepId: 0,
          pivotId: 0,
          pageId: 'start',
        });
      },
    },

    [WorkflowEnum.EstatePlanning]: {
      name: 'Estate Planning',
      icon: 'far fa-clipboard-list-check',
      onClick: () => {
        ADV.util.Router.go2('Workflow', {
          clientId,
          workflowId: WorkflowIdEnum.EstatePlanning,
          stepId: 0,
          pivotId: 0,
          pageId: 'start',
        });
      },
    },

    [WorkflowEnum.Retirement]: {
      name: 'Retirement Savings',
      icon: 'far fa-island-tropical',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('RetirementWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.Education]: {
      name: 'Education',
      icon: 'far fa-graduation-cap',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('EducationWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.DebtManagement]: {
      name: 'Debt Management',
      icon: 'far fa-credit-card',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('DebtManagementWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.HomePurchase]: {
      name: 'Home Purchase',
      icon: 'far fa-home-lg',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('HomePurchaseWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.EmergencyFund]: {
      name: 'Emergency Fund',
      icon: 'far fa-life-ring',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('EmergencyFundWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.HealthCare]: {
      name: 'Health Care',
      icon: 'far fa-hands-heart',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('HealthCareWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.LargePurchase]: {
      name: 'Large Purchase',
      icon: 'fas fa-dollar-sign',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('LargePurchaseWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.FinancialAccounts]: {
      name: 'Add Accounts',
      icon: 'far fa-briefcase',
      onClick: () => {
        ADV.util.Router.go2('FinancialAccountsWorkflow', {
          clientId,
        });
      },
    },

    [WorkflowEnum.RiskTolerance]: {
      name: isHiddenLeversRTQ ? 'Risk Profile' : 'Risk Tolerance',
      description:
        'Understand how you think about risk and find the optimal level for you and your investment strategy.',
      icon: 'far fa-tachometer',
      onClick: () => {
        ADV.util.Router.go2('RiskToleranceWorkflow', {
          clientId,
        });
      },
    },

    [WorkflowEnum.AssetBucketing]: {
      name: assetBucketingHeadline,
      description: 'Visualize and organize your wealth in an intuitive, purposeful way.',
      icon: 'far fa-envelope-open-dollar',
      onClick: () => {
        ADV.util.Router.go2('Workflow', {
          clientId,
          workflowId: WorkflowIdEnum.AssetBucketing,
          stepId: 0,
          pivotId: 0,
          pageId: 'start',
        });
      },
    },

    [WorkflowEnum.BeFi20]: {
      name: 'BeFi20',
      description:
        'Explore and examine your beliefs and values about money and see how they align with others.',
      icon: 'befi20',
      isCustomIcon: true,
      onClick: () => {
        ADV.util.Router.go2('BeFi20Workflow', {
          clientId,
        });
      },
    },

    [WorkflowEnum.Decumulation]: {
      name: 'Decumulation',
      icon: 'far fa-comments-dollar',
      onClick: ({ isCompleted }) => {
        ADV.util.Router.go2('DecumulationWorkflow', {
          clientId,
          goToSummaryWhenReady: isCompleted ? '1' : '0',
        });
      },
    },

    [WorkflowEnum.PulseCheck]: {
      name: 'PulseCheck',
      description:
        'Discover the shape your fulfillment takes and how your financial plan can help you actualize it.',
      icon: 'pulseCheck',
      isCustomIcon: true,
      onClick: () => {
        ADV.util.Router.go2('PulseCheckWorkflow', {
          clientId,
        });
      },
    },

    [WorkflowEnum.CharitableGiving]: {
      name: 'Charitable Giving',
      icon: 'far fa-envelope-open-dollar',
      onClick: () => {
        ADV.util.Router.go2('CharitableGivingWorkflowGoals', {
          clientId,
        });
      },
    },
  };

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    return queryResult.data.reduce(
      (acc, { name: id, markAsCompleted }) => {
        if (FINANCIAL_BEHAVIOUR_WORKFLOWS.includes(id)) {
          return {
            ...acc,
            financialBehaviourWorkflows: [
              ...acc.financialBehaviourWorkflows,
              { id, markAsCompleted, ...workflowTilesMap[id] },
            ],
          };
        }

        return {
          ...acc,
          otherWorkflows: [...acc.otherWorkflows, { id, markAsCompleted, ...workflowTilesMap[id] }],
        };
      },
      { financialBehaviourWorkflows: [] as any[], otherWorkflows: [] as any[] },
    );
  }, [queryResult.data, workflowTilesMap]);

  return {
    ...queryResult,
    data,
    workflowTilesMap,
  };
};
